import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const LS_TOKEN = "bud_consumer_token";
const LS_FAVS = "bud_favorites"; // anonymous favorites — synced to server on sign-in

const ConsumerAuthContext = createContext(null);

const readLocalFavs = () => {
  try {
    const raw = localStorage.getItem(LS_FAVS);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
};

const writeLocalFavs = (ids) => {
  try {
    localStorage.setItem(LS_FAVS, JSON.stringify([...new Set(ids)]));
  } catch {
    /* quota / private mode — silent */
  }
};

export const ConsumerAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set(readLocalFavs()));
  const tokenRef = useRef(localStorage.getItem(LS_TOKEN));

  const authHeader = useCallback(() => {
    const t = tokenRef.current;
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const setToken = (token) => {
    tokenRef.current = token;
    if (token) localStorage.setItem(LS_TOKEN, token);
    else localStorage.removeItem(LS_TOKEN);
  };

  const setFavoritesFromServer = (items) => {
    const ids = new Set((items || []).map((p) => p.id));
    setFavoriteIds(ids);
    writeLocalFavs([...ids]);
  };

  // Initial bootstrap — check existing session
  useEffect(() => {
    // If returning from Emergent OAuth callback, skip /me — let AuthCallback handle it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      if (!tokenRef.current) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/consumer/auth/me", {
          headers: authHeader(),
        });
        if (cancelled) return;
        setUser(data.user);
        // hydrate server favorites
        const { data: fav } = await api.get("/consumer/favorites", {
          headers: authHeader(),
        });
        if (!cancelled) setFavoritesFromServer(fav.favorites);
      } catch {
        if (!cancelled) {
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After login/register/google: persist token, fetch user, sync local favorites
  const completeSignIn = useCallback(
    async ({ session_token, user: u }) => {
      setToken(session_token);
      setUser(u);
      // sync anonymous list → server
      const local = readLocalFavs();
      try {
        const { data: sync } = await api.post(
          "/consumer/favorites/sync",
          { product_ids: local },
          { headers: { Authorization: `Bearer ${session_token}` } }
        );
        setFavoritesFromServer(sync.favorites);
      } catch {
        /* non-fatal: keep local */
      }
    },
    []
  );

  const register = async ({ email, password, name }) => {
    const { data } = await api.post("/consumer/auth/register", {
      email,
      password,
      name,
    });
    await completeSignIn(data);
    toast.success(`Welcome${data.user.username ? `, @${data.user.username}` : ""}!`);
    return data.user;
  };

  const login = async ({ email, password }) => {
    const { data } = await api.post("/consumer/auth/login", { email, password });
    await completeSignIn(data);
    toast.success("Signed in");
    return data.user;
  };

  const beginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirect
    )}`;
  };

  const exchangeGoogleSession = async (session_id) => {
    const { data } = await api.post("/consumer/auth/session", { session_id });
    await completeSignIn(data);
    return data.user;
  };

  const signOut = async () => {
    try {
      await api.post(
        "/consumer/auth/logout",
        {},
        { headers: authHeader() }
      );
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    // Intentionally do NOT clear `favoriteIds` or `localStorage[LS_FAVS]`.
    // Per founder rule: "never get rid of local favorites unless the user
    // explicitly deletes them." Signing out drops the auth session only —
    // the device keeps its anonymous favorites list visible, ready to
    // re-sync next time the user signs in (the server merges on /sync).
    toast.success("Signed out");
  };

  // -- Favorites ---------------------------------------------------------
  const isFavorite = useCallback((pid) => favoriteIds.has(pid), [favoriteIds]);

  const toggleFavorite = useCallback(
    async (product) => {
      const pid = product?.id;
      if (!pid) return;
      const next = new Set(favoriteIds);
      const removing = next.has(pid);
      if (removing) next.delete(pid);
      else next.add(pid);
      setFavoriteIds(next);
      writeLocalFavs([...next]);

      if (!user) return; // anonymous — local only

      try {
        if (removing) {
          await api.delete(`/consumer/favorites/${pid}`, {
            headers: authHeader(),
          });
        } else {
          await api.post(
            `/consumer/favorites/${pid}`,
            {},
            { headers: authHeader() }
          );
        }
      } catch (err) {
        // rollback on server error
        setFavoriteIds(favoriteIds);
        writeLocalFavs([...favoriteIds]);
        toast.error("Could not update favorites — try again");
      }
    },
    [favoriteIds, user, authHeader]
  );

  const refreshFavorites = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/consumer/favorites", {
        headers: authHeader(),
      });
      setFavoritesFromServer(data.favorites);
      return data.favorites;
    } catch {
      return [];
    }
  }, [user, authHeader]);

  const refreshUser = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const { data } = await api.get("/consumer/auth/me", {
        headers: authHeader(),
      });
      setUser(data.user);
    } catch {
      // ignore — session likely stale
    }
  }, [authHeader]);

  const value = useMemo(
    () => ({
      user,
      loading,
      favoriteIds,
      favoriteCount: favoriteIds.size,
      isFavorite,
      toggleFavorite,
      refreshFavorites,
      refreshUser,
      register,
      login,
      beginGoogle,
      exchangeGoogleSession,
      signOut,
      authHeader,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, favoriteIds, isFavorite, toggleFavorite, refreshFavorites, refreshUser]
  );

  return (
    <ConsumerAuthContext.Provider value={value}>
      {children}
    </ConsumerAuthContext.Provider>
  );
};

export const useConsumerAuth = () => {
  const ctx = useContext(ConsumerAuthContext);
  if (!ctx)
    throw new Error("useConsumerAuth must be used within ConsumerAuthProvider");
  return ctx;
};
